import { Box } from "@repo/components/src/ui/box";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Image } from "@repo/components/src/ui/image";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { formatSimpleMoney } from "@repo/components/src/utils/money";
import { ProductsSelectionQuery } from "@repo/graphql/documents/product/query.generated";
import { Package } from "lucide-react-native";
import React from "react";

type ProductNode =
  ProductsSelectionQuery["product"]["list"]["edges"][number]["node"];

type Props = {
  product: ProductNode;
  quantity: number;
};

/** Ported from `apps/mobil/.../terms/PriceProductRow.tsx`. The mobile version
 * reads a server-computed `totalAmount` off `campaignpriceproductSet` — a draft
 * has no such row yet, so the total is `unitPrice × quantity`, computed here. */
const PriceProductRow = React.memo(({ product, quantity }: Props) => {
  const total =
    product.price && Number(product.price.amount) > 0
      ? formatSimpleMoney({
          amount: String(Number(product.price.amount) * quantity),
          currency: product.price.currency.code,
        })
      : null;

  return (
    <HStack
      space="md"
      className="px-4 py-3 items-center border-t border-outline-50"
    >
      <Box className="rounded-xl overflow-hidden items-center justify-center w-12 h-12 bg-background-100">
        {product.image?.url ? (
          <Image
            source={{ uri: product.image.url }}
            alt={product.name}
            className="w-full h-full"
            contentFit="cover"
          />
        ) : (
          <Icon as={Package} size="md" className="text-typography-400" />
        )}
      </Box>
      <VStack className="flex-1" space="xs">
        <Text
          bold
          size="sm"
          className="text-typography-950 tracking-tight"
          numberOfLines={2}
        >
          {product.name}
        </Text>
        {quantity > 1 ? (
          <Text size="xs" className="text-typography-500">
            {`Cantidad · ${quantity}`}
          </Text>
        ) : null}
      </VStack>
      {total ? (
        <Text bold size="sm" className="text-typography-900">
          {total}
        </Text>
      ) : null}
    </HStack>
  );
});

PriceProductRow.displayName = "PriceProductRow";

export default PriceProductRow;
